import { Merchant } from "@/dto/merchant";
import { ScanToOrderCartHeader } from "./scanToOrderCartHeader";
import { SendToKitchenButton } from "./sendToKitchenButton";
import { TableData } from "@/dto/tableData";
import { ScanToOrderCartContent } from "./scanToOrderCartContent";
import ReCAPTCHAComponent from "react-google-recaptcha";
import {
  getFromLocalStorage,
  saveToLocalStorage,
} from "@/utils/localStorageUtils";
import { RetailCartItem } from "@/dto/retailCartItem";
import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { ScanToOrderInfo } from "@/dto/scanToOrderInfo";
import {
  getCartButtonText,
  goToGuestCheckout,
  sendToKitchen,
} from "@/utils/scanToOrderUtils";
import { useRouter } from "next13-progressbar";
import { LocalStorageKey } from "@/utils/storageKeys";
import { DrawerModal } from "@/components/modals/modals";
import { cloverColorTheme } from "@/utils/cloverColorTheme";
import { isBusinessOpen } from "@/utils/isBusinessOpenUtil";

export interface ScanToOrderCartProps {
  isOpen: boolean;
  onRequestClose: () => void;
  merchant: Merchant;
  tableData: TableData;
  setIsSentToKitchen: Dispatch<SetStateAction<boolean>>;
  scanToOrderInfo?: ScanToOrderInfo;
  isRunningTabsEnabled: boolean;
}

export const ScanToOrderCart = ({
  isOpen,
  onRequestClose,
  merchant,
  tableData,
  setIsSentToKitchen,
  scanToOrderInfo,
  isRunningTabsEnabled,
}: ScanToOrderCartProps) => {
  const orderSpecialInstructions = tableData?.order?.note;
  const containsSpecialInstructions = !!tableData?.order?.note;
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showSpecialInstructions, setShowSpecialInstructions] = useState(
    containsSpecialInstructions ? true : false
  );
  const [failedToSendToKitchen, setFailedToSendToKitchen] =
    useState<boolean>(false);
  const recaptchaKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || "";
  const [specialInstructions, setSpecialInstructions] = useState(
    containsSpecialInstructions ? orderSpecialInstructions : ""
  );
  const merchantColor =
    merchant.merchantMeta?.colorPalette?.color ||
    cloverColorTheme.Main.primaryColor;
  const [cartItems, setCartItems] = useState(() => {
    return getFromLocalStorage<RetailCartItem[]>(LocalStorageKey.CART, []);
  });
  const router = useRouter();

  const captchaRef = useRef<ReCAPTCHAComponent | null>(null);

  const currentItemCount = cartItems.reduce((totalQuantity, item) => {
    const quantity = item.value?.quantity || 0;
    return totalQuantity + quantity;
  }, 0);

  const sentToKitchenCart = tableData?.order?.cart
    ? tableData?.order?.cart?.items
    : [];

  const isBusinessClosed = !isBusinessOpen(merchant, true);
  const isButtonDisabled =
    (sentToKitchenCart?.length === 0 && cartItems?.length === 0) ||
    isBusinessClosed ||
    isLoading;

  const buttonText = getCartButtonText(
    currentItemCount,
    merchant.isRunningTabsEnabled
  );

  const emptyCart = currentItemCount === 0;

  useEffect(() => {
    const storedCart = getFromLocalStorage<RetailCartItem[]>(
      LocalStorageKey.CART,
      []
    );
    setCartItems(storedCart);
    setFailedToSendToKitchen(false);
  }, [isOpen]);

  useEffect(() => {
    saveToLocalStorage(LocalStorageKey.CART, cartItems);
  }, [cartItems]);

  if (!scanToOrderInfo) {
    return null;
  }
  const { orderType, tableId, employeeId } = scanToOrderInfo;

  const removeFromCart = (event: React.MouseEvent, index: number) => {
    event.stopPropagation();
    setCartItems((prevCartItems: any[]) =>
      prevCartItems.filter((_: any, i: any) => i !== index)
    );
  };

  async function onRecaptchaSubmit(value: any) {
    await sendToKitchen(
      merchant,
      value,
      cartItems,
      specialInstructions,
      tableId,
      merchant.id,
      tableData,
      onRequestClose,
      setIsSentToKitchen,
      setFailedToSendToKitchen,
      setIsLoading,
      employeeId
    );
  }

  const initiateCaptcha = () => {
    setIsLoading(true);
    captchaRef.current?.reset();
    captchaRef.current?.execute();
  };

  return (
    <DrawerModal isOpen={isOpen} onRequestClose={onRequestClose}>
      <ReCAPTCHAComponent
        className=""
        sitekey={recaptchaKey}
        ref={captchaRef}
        onChange={onRecaptchaSubmit}
        size="invisible"
        badge="bottomleft"
      />
      <ScanToOrderCartHeader onRequestClose={onRequestClose} />
      <ScanToOrderCartContent
        tableData={tableData}
        cartItems={cartItems}
        showSpecialInstructions={showSpecialInstructions}
        setShowSpecialInstructions={setShowSpecialInstructions}
        merchantColor={merchantColor}
        setSpecialInstructions={setSpecialInstructions}
        removeFromCart={removeFromCart}
        orderType={orderType}
        isCartEmpty={emptyCart}
        isRunningTabsEnabled={isRunningTabsEnabled}
        failedToSendToKitchen={failedToSendToKitchen}
        onRequestClose={onRequestClose}
        specialInstructions={specialInstructions}
        isBusinessClosed={isBusinessClosed}
      />
      <style>
        {`
        .grecaptcha-badge { margin-bottom: 50px; }
        `}
      </style>
      <SendToKitchenButton
        buttonText={buttonText}
        merchantColor={merchantColor}
        isButtonDisabled={isButtonDisabled}
        onClick={
          merchant?.isRunningTabsEnabled && !emptyCart
            ? initiateCaptcha
            : () => goToGuestCheckout(router, scanToOrderInfo)
        }
      />
    </DrawerModal>
  );
};
